import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import DropDown from "../DropDown";
import { DropDownItem } from "../../atoms/DropDownItem";
import classNames from "../../../utils/classNames";
import "./SettingsLayout.scss";

const SettingsSelect = ({ label, description, value, onChange, options, name, disabled }) => {
    const generatedId = useId();
    const controlId = name ? `${name}-${generatedId}` : generatedId;
    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    /* The menu is portaled out to #portal, so it is not inside containerRef —
       the outside-click check has to know about it separately. */
    const dropdownRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const selectedOption = useMemo(
        () => options.find((option) => option.value === value) || options[0] || null,
        [options, value],
    );

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handlePointerDown = (event) => {
            const clickedInsideField = containerRef.current?.contains(event.target);
            const clickedInsideMenu = dropdownRef.current?.contains(event.target);

            /* Closing on mousedown would unmount the menu before mouseup, so the
               option's click never fires and the selection is silently dropped. */
            if (!clickedInsideField && !clickedInsideMenu) {
                setIsOpen(false);
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setIsOpen(false);
                triggerRef.current?.focus();
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("touchstart", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("touchstart", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen]);

    useEffect(() => {
        if (disabled && isOpen) {
            setIsOpen(false);
        }
    }, [disabled, isOpen]);

    useEffect(() => {
        const sectionElement = containerRef.current?.closest(".settings-section");

        if (!sectionElement) {
            return undefined;
        }

        sectionElement.classList.toggle("settings-section--open", isOpen);

        return () => {
            sectionElement.classList.remove("settings-section--open");
        };
    }, [isOpen]);

    const handleSelect = (nextValue) => {
        onChange(nextValue);
        setIsOpen(false);
        const focusTrigger = () => {
            triggerRef.current?.focus();
        };

        if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
            window.requestAnimationFrame(focusTrigger);
            return;
        }

        focusTrigger();
    };

    const handleTriggerKeyDown = (event) => {
        if (disabled) {
            return;
        }

        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setIsOpen(true);
        }
    };

    return (
        <div className={classNames("settings-option", "settings-option--select", { "settings-option--open": isOpen })}>
            <div className="settings-option__details">
                <span className="settings-option__label" id={`${controlId}-label`}>
                    {label}
                </span>
                {description && (
                    <span className="settings-option__description" id={`${controlId}-description`}>
                        {description}
                    </span>
                )}
            </div>
            <div className="settings-option__control settings-option__control--select" ref={containerRef}>
                <button
                    id={controlId}
                    ref={triggerRef}
                    type="button"
                    name={name}
                    disabled={disabled}
                    aria-labelledby={`${controlId}-label`}
                    aria-describedby={description ? `${controlId}-description` : undefined}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    className={classNames("settings-option__select-trigger", { "is-open": isOpen })}
                    onClick={() => setIsOpen((previous) => !previous)}
                    onKeyDown={handleTriggerKeyDown}
                >
                    <span className="settings-option__select-value">{selectedOption?.label}</span>
                    <span className="settings-option__select-caret" aria-hidden="true">▾</span>
                </button>
                {isOpen && (
                    /* Portaled and viewport-positioned: the settings body scrolls
                       and each section clips its own group, either of which used
                       to cut the menu off for rows near an edge. */
                    <DropDown
                        anchorRef={triggerRef}
                        dropdownRef={dropdownRef}
                        items={options}
                        className="settings-option__menu"
                        menuRole="listbox"
                        noItemsMessage=""
                        placement="bottom-start"
                        matchTriggerWidth
                        strategy="fixed"
                        renderInPortal
                        getItemId={(option) => option.value}
                        renderItem={(option) => (
                            <DropDownItem
                                key={option.value}
                                className={classNames("settings-option__menu-item", {
                                    "is-selected": option.value === selectedOption?.value,
                                })}
                                role="option"
                                aria-selected={option.value === selectedOption?.value}
                                onClick={() => handleSelect(option.value)}
                            >
                                {option.label}
                            </DropDownItem>
                        )}
                    />
                )}
            </div>
        </div>
    );
};

SettingsSelect.propTypes = {
    label: PropTypes.node.isRequired,
    description: PropTypes.node,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.node.isRequired,
        value: PropTypes.string.isRequired,
    })).isRequired,
    name: PropTypes.string,
    disabled: PropTypes.bool,
};

SettingsSelect.defaultProps = {
    description: "",
    name: undefined,
    disabled: false,
};

export default SettingsSelect;
